import { useFormContext } from "react-hook-form";
import { AppState } from "../../RootReducer";
import { useSelector } from "react-redux";
import { validateCryptoAddress, AddressValidationResultCode, toCanonicalSymbol } from "../../payments/CryptoAddressValidator";
import { t } from "../../i18n";
import TextField from "../../layout/TextField";
import { useEffect, useState } from "react";
import { CryptoFormData } from "./CryptoDetails";
import { getContainer } from "../../ioc/IOCSetup";

const CryptoAddressInput: React.FC = () => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CryptoFormData>();
  const withdraw = useSelector((state: AppState) => state.payments.withdraw);
  const methodCode = withdraw?.activeMethod?.code;
  const context = withdraw?.withdrawContext;
  const btcLn = methodCode === "BTC_LIGHTNING";

  const [warningMessage, setWarningMessage] = useState<string>();
  const recipientAddress = watch("recipientAddress");

  useEffect(() => {
    if (!recipientAddress) return;

    const currencyCode = context?.cryptoContext?.network ?? withdraw?.activeCurrency!;
    const validation = validateCryptoAddress(recipientAddress, currencyCode, true, btcLn);

    // We never block the user from continuing, but we show a warning, if the address is invalid or wrong currency
    if (validation.result === AddressValidationResultCode.VALID) {
      return setWarningMessage(undefined);
    }
    if (btcLn) {
      return setWarningMessage(t("payments.invalid-crypto-btc-lightning-invoice"));
    }
    if (validation.result === AddressValidationResultCode.WRONG_CURRENCY) {
      return setWarningMessage(
        t("payments.invalid-crypto-address-guess", {
          code: toCanonicalSymbol(currencyCode).toUpperCase(),
          guessed: validation.guessedCurrencies?.join(", ").toUpperCase(),
        }),
      );
    }

    setWarningMessage(t("payments.invalid-crypto-address", { code: toCanonicalSymbol(currencyCode).toUpperCase() }));
  }, [recipientAddress, context?.cryptoContext?.network, withdraw?.activeCurrency, btcLn]);

  useEffect(() => {
    const getStoredAddress = async () => {
      let key = `${methodCode}-${withdraw?.activeCurrency}-withdrawAddress`;
      const data = await getContainer().getUserService().getUserSettings(key);
      const { address, tag } = getAddressAndTag("" + data);
      setValue("recipientAddress", address);
      setValue("destinationTag", tag);
    };

    getStoredAddress();
  }, [methodCode, withdraw?.activeCurrency]);

  return (
    <div className="crypto-address-input">
      {btcLn && (
        <div className="help-text-wrapper mb-2">
          <span className="help-text">{t("payments.btc-lightning-warning")}</span>
        </div>
      )}

      <TextField
        {...register("recipientAddress", {
          required: t("withdraw.withdrawEmptyError"),
        })}
        label={t("withdrawPage.addressPhaseLabel")}
        type={withdraw?.recipientAddress?.startsWith("ln") ? "textarea" : "text"}
        errorText={warningMessage}
        placeholder={btcLn ? t("payments.btc-lightning-invoice") : t("withdrawPage.addressPhasePlaceholder")}
      />

      {methodCode === "XRP" && (
        <>
          <div className="mt-2">
            <TextField
              {...register("destinationTag", {
                validate: (value) => {
                  if (!value) return true;
                  return "" + parseInt(value) === value || t("payments.invalid-destination-tag");
                },
              })}
              label={t("payments.destination-tag")}
              type="text"
              errorText={errors.destinationTag?.message}
              placeholder={t("payments.destination-tag-optional")}
            />
          </div>
        </>
      )}

      {methodCode === "TON" && (
        <TextField {...register("notesTag")} type="text" placeholder={t("payments.notes-tag-optional")} label={t("payments.notes-tag-optional")} />
      )}
    </div>
  );
};

export default CryptoAddressInput;

function getAddressAndTag(data: string): { address: string; tag: string } {
  let address = data;
  let tag = "";
  if (data && data.length > 0 && data.indexOf(":") !== -1) {
    let parts = data.split(":");
    address = parts[0];
    tag = parts[1];
  }
  return { address, tag };
}